/**
 * Rate List purpose:
 *   Per category + customer segment pricing
 *   - By Gram        → labour (rlg_per_gm_labour) + code
 *   - By MRP         → discount %
 *   - By Silver Rate → tunch %
 *
 * Sell uses this map:
 *   Wholesale       → Wholesale
 *   Retailer        → Jeweller
 *   Direct Customer → Direct
 *   Family / Friend → no Rate List (category fallback)
 */

export const RATE_LIST_CUSTOMER_TYPES = ['Wholesale', 'Jeweller', 'Direct'] as const;
export type RateListCustomerType = (typeof RATE_LIST_CUSTOMER_TYPES)[number];

/** Map customer type → Rate List type. null = do not apply Rate List. */
export function mapUserCustomerTypeToRateListType(
  userCustomerType: string | null | undefined
): RateListCustomerType | null {
  switch (String(userCustomerType || '').trim().toLowerCase()) {
    case 'wholesale':
      return 'Wholesale';
    case 'retailer':
      return 'Jeweller';
    case 'direct customer':
    case 'direct':
      return 'Direct';
    default:
      return null;
  }
}

/** "All" weight band (Above+Below together, or rlg_weight_range = All). */
export function isRateListAllWeightRange(item: any): boolean {
  const above = String(item?.rlg_above || '').toLowerCase() === 'yes';
  const below = String(item?.rlg_below || '').toLowerCase() === 'yes';
  return (above && below) || String(item?.rlg_weight_range || '').toLowerCase() === 'all';
}

/** Does this By Gram rate row apply to weight? */
export function matchesRateListWeight(item: any, weight: number): boolean {
  if (isRateListAllWeightRange(item)) {
    return true;
  }

  const minRaw = item?.rlg_min_weight_range;
  const maxRaw = item?.rlg_max_weight_range;
  const min = minRaw === null || minRaw === undefined || minRaw === '' ? NaN : Number(minRaw);
  const max = maxRaw === null || maxRaw === undefined || maxRaw === '' ? NaN : Number(maxRaw);
  const above = String(item?.rlg_above || '').toLowerCase() === 'yes';
  const below = String(item?.rlg_below || '').toLowerCase() === 'yes';

  if (!isNaN(min) && !isNaN(max)) return weight >= min && weight <= max;
  if (above && !isNaN(min)) return weight >= min;
  if (below && !isNaN(max)) return weight <= max;
  return false;
}

/** Prefer Custom / Above / Below over All when several rows match. */
export function pickBestRateListMatch(matches: any[]): any | null {
  if (!matches?.length) return null;

  return [...matches].sort((a, b) => {
    const aAll = isRateListAllWeightRange(a);
    const bAll = isRateListAllWeightRange(b);
    if (aAll !== bAll) return aAll ? 1 : -1;

    const aMin =
      a?.rlg_min_weight_range === null || a?.rlg_min_weight_range === undefined || a?.rlg_min_weight_range === ''
        ? -Infinity
        : Number(a.rlg_min_weight_range);
    const bMin =
      b?.rlg_min_weight_range === null || b?.rlg_min_weight_range === undefined || b?.rlg_min_weight_range === ''
        ? -Infinity
        : Number(b.rlg_min_weight_range);
    if (aMin !== bMin) return bMin - aMin;

    const aMax =
      a?.rlg_max_weight_range === null || a?.rlg_max_weight_range === undefined || a?.rlg_max_weight_range === ''
        ? Infinity
        : Number(a.rlg_max_weight_range);
    const bMax =
      b?.rlg_max_weight_range === null || b?.rlg_max_weight_range === undefined || b?.rlg_max_weight_range === ''
        ? Infinity
        : Number(b.rlg_max_weight_range);
    return aMax - bMax;
  })[0];
}
